"use client";
import AppLoading from "@/components/app-loading";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import useAsync from "./use-async";
import { DashboardService } from "@/services/DashboardServices";
import { SelectOptionProps } from "@/components/form/CustmSelect";
import { SuiviDashboardService } from "@/services/SuiviDashboardServices";
import { signOut, useSession } from "next-auth/react";
import AuthServices from "@/services/auth";
import { useDispatch } from "react-redux";
import { errorNotification } from "@/utils/functions";
import { saveToken, saveUser } from "@/store/authSlice";
import { useAuthStore } from "@/store/authStore";

type Disease = {
  id: string;
  name: string;
  slug?: string;
  key?: string;
  label?: string;
  secteur?: string;
};
interface AppProviderProps {
  children: ReactNode;
}

interface DashboardContextProps {
  diseases: SelectOptionProps[];
  impacts: SelectOptionProps[];
  partners: SelectOptionProps[];
  provinces: SelectOptionProps[];
  provincesList: any[];
}

const DashboardContext = createContext<DashboardContextProps>({
  diseases: [],
  impacts: [],
  partners: [],
  provinces: [],
  provincesList: [],
});
export const useDashboardContext = () => {
  return useContext(DashboardContext);
};
const DashboardProvider = ({ children }: AppProviderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const dispatch = useDispatch();
  const { data: session, status } = useSession();
  const { data: diseases, loading } = useAsync<Disease | any>(() =>
    DashboardService.getMaladie()
  );

  const { data: impactList, loading: loadingImpact } = useAsync<Disease | any>(
    () => SuiviDashboardService.getImpacts()
  );
  const { data: organizationsList, loading: loadingOrganisation } = useAsync<
    Disease | any
  >(() => SuiviDashboardService.getOrganizations());

  const { data: provincesList, loading: loadingProvince } = useAsync<
    Disease | any
  >(() => DashboardService.getProvinces());

  useEffect(() => {
    const getAuthGoogle = async () => {
      if (status === "authenticated" && session?.user && !isAuthenticated) {
        const { name, email, image }: any = session.user;
        try {
          setIsLoading(true);
          const response = await AuthServices.loginProvider({
            email,
            profil: image,
            full_name: name,
          });
          dispatch(saveToken({ accessToken: response.data.token }));
          dispatch(saveUser(response.data?.data));
        } catch (err: any) {
          const message =
            err?.response?.data?.message || err.message || "Erreur inconnue";
          errorNotification(message);
        } finally {
          await signOut({ redirect: false });
          setIsLoading(false);
        }
      }
    };

    getAuthGoogle();
  }, [session, status, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const getProfile = async () => {
      try {
        setIsLoading(true);
        const response = await AuthServices.getCurrentUser();
        dispatch(saveUser(response.data?.data));
      } catch (err: any) {
        const message =
          err?.response?.data?.message || err.message || "Erreur inconnue";
      } finally {
        await signOut({ redirect: false });
        setIsLoading(false);
      }
    };

    getProfile();
  }, [isAuthenticated]);

  return (
    <DashboardContext.Provider
      value={{
        diseases: diseases?.map(
          (item: Disease): SelectOptionProps => ({
            value: item?.name,
            label: item?.name,
          })
        ),
        partners: organizationsList?.map(
          (item: Disease): SelectOptionProps => ({
            value: item?.slug,
            label: item?.name,
          })
        ),
        impacts: impactList?.map(
          (item: Disease): SelectOptionProps => ({
            value: item?.key,
            label: item?.label,
          })
        ),
        provinces: provincesList?.map(
          (item: Disease): SelectOptionProps => ({
            value: item?.name?.toLowerCase(),
            label: item?.name,
          })
        ),
        provincesList,
      }}
    >
      {/* ROLLBACK NOTE: Uncomment below to restore blocking full-screen loading on initial app boot
      isLoading ||
      loading ||
      loadingImpact ||
      loadingOrganisation ||
      loadingProvince ? (
        <AppLoading />
      ) : (
        children
      ) */}
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardProvider;
